import { PERCENTAGE_MAX } from "@/constants/percentage.constants";
import { UtteranceType } from "../utterances/utterances.utility.decl";

import { TalkRatio } from "./talk_ratio.utility.decl";

export function computeTalkRatio(
  utterances?: (UtteranceType | null)[] | null
): TalkRatio {
  const list = utterances ?? [];

  const [map, total] = list.reduce(
    ([accumulatedMap, accumulatedTotal], utterance) => {
      if (!utterance) {
        return [accumulatedMap, accumulatedTotal];
      }

      const sum = utterance.endTime - utterance.startTime;

      if (!accumulatedMap.has(utterance.channel)) {
        accumulatedMap.set(utterance.channel, sum);
        return [accumulatedMap, accumulatedTotal + sum];
      }

      accumulatedMap.set(
        utterance.channel,
        accumulatedMap.get(utterance.channel)! + sum
      );

      return [accumulatedMap, accumulatedTotal + sum];
    },
    [new Map<number, number>(), 0]
  );

  if (total === 0) {
    return new Map();
  }

  // Largest-remainder (Hamilton) apportionment: floor every channel's exact
  // share, then hand the leftover points to the largest fractional remainders
  // so the displayed integers always sum to exactly 100.
  const shares = [...map.entries()].map(([key, value], index) => {
    const raw = (value / total) * PERCENTAGE_MAX;
    const floor = Math.floor(raw);

    return { key, floor, remainder: raw - floor, index };
  });

  const leftover =
    PERCENTAGE_MAX -
    shares.reduce((accumulated, share) => accumulated + share.floor, 0);

  const rankedForLeftover = [...shares].sort(
    (a, b) => b.remainder - a.remainder || a.index - b.index
  );

  for (let position = 0; position < leftover; position += 1) {
    rankedForLeftover[position].floor += 1;
  }

  return new Map(shares.map((share) => [share.key, share.floor]));
}
